import React, { useState } from 'react';

// Sehr kleiner Markdown-Renderer. Baut React-Elemente statt HTML-Strings, es wird
// also nirgends dangerouslySetInnerHTML benutzt - Modellausgaben koennen damit
// kein Markup einschleusen.

const INLINE_PATTERN = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)\s]+\))/g;

function renderInline(text, keyPrefix) {
    const parts = text.split(INLINE_PATTERN).filter(Boolean);

    return parts.map((part, index) => {
        const key = `${keyPrefix}-${index}`;

        if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
            return <code key={key} className="md-inline-code">{part.slice(1, -1)}</code>;
        }
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
            return <strong key={key}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
            return <em key={key}>{part.slice(1, -1)}</em>;
        }

        const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
        if (link) {
            const href = link[2];
            // Nur http(s) und mailto zulassen (kein javascript:).
            if (/^(https?:|mailto:)/i.test(href)) {
                return (
                    <a key={key} href={href} target="_blank" rel="noopener noreferrer">
                        {link[1]}
                    </a>
                );
            }
            return <span key={key}>{link[1]}</span>;
        }

        return <span key={key}>{part}</span>;
    });
}

function CodeBlock({ language, code }) {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* Zwischenablage nicht verfuegbar */
        }
    };

    return (
        <div className="md-code">
            <div className="md-code-head">
                <span>{language || 'Code'}</span>
                <button type="button" onClick={copy}>
                    {copied ? 'Kopiert' : 'Kopieren'}
                </button>
            </div>
            <pre>
                <code>{code}</code>
            </pre>
        </div>
    );
}

/** Nicht-Code-Abschnitt in Ueberschriften, Listen, Zitate und Absaetze zerlegen. */
function renderProse(text, keyPrefix) {
    const lines = text.split('\n');
    const blocks = [];
    let paragraph = [];
    let list = null; // { ordered, items: [] }

    const flushParagraph = () => {
        if (!paragraph.length) return;
        const key = `${keyPrefix}-p${blocks.length}`;
        blocks.push(<p key={key}>{renderInline(paragraph.join(' '), key)}</p>);
        paragraph = [];
    };

    const flushList = () => {
        if (!list) return;
        const key = `${keyPrefix}-l${blocks.length}`;
        const Tag = list.ordered ? 'ol' : 'ul';
        blocks.push(
            <Tag key={key}>
                {list.items.map((item, index) => (
                    <li key={`${key}-${index}`}>{renderInline(item, `${key}-${index}`)}</li>
                ))}
            </Tag>
        );
        list = null;
    };

    lines.forEach((rawLine) => {
        const line = rawLine.trimEnd();

        if (!line.trim()) {
            flushParagraph();
            flushList();
            return;
        }

        const heading = /^(#{1,4})\s+(.*)$/.exec(line);
        if (heading) {
            flushParagraph();
            flushList();
            const key = `${keyPrefix}-h${blocks.length}`;
            const Tag = `h${Math.min(heading[1].length + 2, 6)}`;
            blocks.push(<Tag key={key}>{renderInline(heading[2], key)}</Tag>);
            return;
        }

        const quote = /^>\s?(.*)$/.exec(line);
        if (quote) {
            flushParagraph();
            flushList();
            const key = `${keyPrefix}-q${blocks.length}`;
            blocks.push(<blockquote key={key}>{renderInline(quote[1], key)}</blockquote>);
            return;
        }

        const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
        const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
        if (bullet || numbered) {
            flushParagraph();
            const ordered = Boolean(numbered);
            if (!list || list.ordered !== ordered) {
                flushList();
                list = { ordered, items: [] };
            }
            list.items.push((bullet || numbered)[1]);
            return;
        }

        flushList();
        paragraph.push(line);
    });

    flushParagraph();
    flushList();
    return blocks;
}

export default function Markdown({ content }) {
    if (!content) return null;

    // Auf ``` aufteilen: gerade Indizes = Text, ungerade = Codeblock.
    const segments = content.split(/```/);

    return (
        <div className="md">
            {segments.map((segment, index) => {
                if (index % 2 === 1) {
                    const newline = segment.indexOf('\n');
                    const language = newline === -1 ? '' : segment.slice(0, newline).trim();
                    const code = newline === -1 ? segment : segment.slice(newline + 1);
                    return <CodeBlock key={`c-${index}`} language={language} code={code.replace(/\n$/, '')} />;
                }
                return <React.Fragment key={`t-${index}`}>{renderProse(segment, `t${index}`)}</React.Fragment>;
            })}
        </div>
    );
}
